// SidePanel for managing "Stammdaten-Ordner" (manual entity groups).
//
// Two modes:
//   - "assign":  bulk-assign a set of selected records into a group
//                (or create a new group with those records as initial
//                members). Opened from the BulkActionBar.
//   - "manage":  CRUD list of groups for the current resource type.
//                Opened from the toolbar next to the group-by dropdown.
//
// Both modes share the create-group form and the color palette so the
// admin sees consistent UI.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, FolderPlus, Loader2, Pencil, Plus, Trash2, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { api, type EntityGroupSummary, type EntityResourceType } from '../lib/api'
import { notify } from '../lib/notify'
import { cn } from '../lib/utils'
import { groupColorClass } from './data-table'
import { Button } from './ui/button'
import { confirm } from './ui/confirm-dialog'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { SidePanel } from './ui/side-panel'

/** Fixed admin-side palette. Stored as a plain string in the DB; the
 *  rendering side uses `groupColorClass` to translate to a tailwind
 *  utility. Keep this list aligned with that switch statement. */
const PALETTE = [
  { value: null, label: 'Keine' },
  { value: 'red', label: 'Rot' },
  { value: 'orange', label: 'Orange' },
  { value: 'amber', label: 'Bernstein' },
  { value: 'emerald', label: 'Grün' },
  { value: 'sky', label: 'Himmelblau' },
  { value: 'indigo', label: 'Indigo' },
  { value: 'violet', label: 'Violett' },
  { value: 'rose', label: 'Rosa' },
] as const

const RESOURCE_PLURAL: Record<EntityResourceType, string> = {
  customer: 'Kunden',
  project: 'Projekte',
  product: 'Produkte',
  service: 'Tätigkeiten',
  logbook_vehicle: 'Fahrzeuge',
}

const RESOURCE_INVALIDATE: Record<EntityResourceType, readonly unknown[]> = {
  customer: ['customers'],
  project: ['projects'],
  product: ['products'],
  service: ['services'],
  logbook_vehicle: ['logbook', 'vehicles'],
}

interface CommonProps {
  open: boolean
  onClose: () => void
  resourceType: EntityResourceType
}

type ManualGroupPanelProps =
  | (CommonProps & {
      mode: 'assign'
      /** Selected record ids to bulk-assign / use as initial members. */
      resourceIds: string[]
    })
  | (CommonProps & {
      mode: 'manage'
      resourceIds?: never
    })

export function ManualGroupPanel(props: ManualGroupPanelProps) {
  const { open, onClose, resourceType, mode } = props
  const resourceIds = mode === 'assign' ? props.resourceIds : []
  const queryClient = useQueryClient()

  const groupsQuery = useQuery({
    queryKey: ['groups', resourceType],
    queryFn: () => api.listEntityGroups(resourceType),
    enabled: open,
  })
  const groups = groupsQuery.data?.groups ?? []

  // Reset transient UI state when the panel re-opens.
  const [showCreate, setShowCreate] = useState(false)
  const [newName, setNewName] = useState('')
  const [newColor, setNewColor] = useState<string | null>(null)
  const [renameId, setRenameId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')

  useEffect(() => {
    if (!open) return
    setShowCreate(false)
    setNewName('')
    setNewColor(null)
    setRenameId(null)
    setRenameValue('')
  }, [open])

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['groups', resourceType] })
    queryClient.invalidateQueries({ queryKey: RESOURCE_INVALIDATE[resourceType] })
  }

  const createGroup = useMutation({
    mutationFn: () =>
      api.createEntityGroup(resourceType, {
        name: newName.trim(),
        color: newColor,
        memberIds: mode === 'assign' ? resourceIds : undefined,
      }),
    onSuccess: () => {
      invalidateAll()
      notify.success(
        mode === 'assign'
          ? `Gruppe „${newName.trim()}“ angelegt mit ${resourceIds.length} ${RESOURCE_PLURAL[resourceType]}`
          : `Gruppe „${newName.trim()}“ angelegt`,
      )
      if (mode === 'assign') {
        onClose()
      } else {
        setShowCreate(false)
        setNewName('')
        setNewColor(null)
      }
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const addMembers = useMutation({
    mutationFn: (groupId: string) => api.addEntityGroupMembers(groupId, resourceIds),
    onSuccess: (r, groupId) => {
      const grp = groups.find((g) => g.id === groupId)
      invalidateAll()
      notify.success(
        `${r.added} ${RESOURCE_PLURAL[resourceType]} zur Gruppe „${grp?.name ?? ''}“ hinzugefügt`,
      )
      onClose()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const renameGroup = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => api.updateEntityGroup(id, { name }),
    onSuccess: () => {
      invalidateAll()
      notify.success('Gruppe umbenannt')
      setRenameId(null)
      setRenameValue('')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const changeColor = useMutation({
    mutationFn: ({ id, color }: { id: string; color: string | null }) =>
      api.updateEntityGroup(id, { color }),
    onSuccess: () => {
      invalidateAll()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const removeGroup = useMutation({
    mutationFn: (id: string) => api.deleteEntityGroup(id),
    onSuccess: () => {
      invalidateAll()
      notify.success('Gruppe gelöscht')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const title =
    mode === 'assign'
      ? `${resourceIds.length} ${RESOURCE_PLURAL[resourceType]} einer Gruppe zuordnen`
      : 'Gruppen verwalten'
  const description =
    mode === 'assign'
      ? 'Wähle eine bestehende Gruppe oder lege eine neue an.'
      : 'Manuelle Gruppen (Ordner) für Stammdaten — nur für Admins sichtbar.'

  const canSubmitCreate = newName.trim().length > 0 && !createGroup.isPending

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      footer={
        mode === 'assign' ? (
          <Button variant="ghost" onClick={onClose} type="button">
            Schließen
          </Button>
        ) : (
          <Button variant="ghost" onClick={onClose} type="button">
            Fertig
          </Button>
        )
      }
    >
      <div className="space-y-4 pb-2">
        {/* Create-group section — collapsible to reduce visual noise. */}
        {!showCreate && (
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="flex w-full items-center gap-2 rounded-lg border border-dashed border-border-default px-3 py-2.5 text-sm text-text-secondary hover:border-accent/50 hover:text-text-primary hover:bg-bg-overlay/40 transition-colors"
          >
            <FolderPlus className="h-4 w-4 text-accent" />
            <span>Neue Gruppe anlegen</span>
            {mode === 'assign' && (
              <span className="ml-auto text-[11px] text-text-tertiary tabular-nums">
                {resourceIds.length} {RESOURCE_PLURAL[resourceType]}
              </span>
            )}
          </button>
        )}
        {showCreate && (
          <div className="rounded-lg border border-border-default bg-bg-overlay/30 p-3 space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="manual-group-name">Name</Label>
              <Input
                id="manual-group-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="z.B. Stammkunden"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && canSubmitCreate) {
                    e.preventDefault()
                    createGroup.mutate()
                  }
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Farbe (optional)</Label>
              <ColorSwatchPicker value={newColor} onChange={setNewColor} />
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={() => {
                  setShowCreate(false)
                  setNewName('')
                  setNewColor(null)
                }}
              >
                Abbrechen
              </Button>
              <Button
                size="sm"
                type="button"
                disabled={!canSubmitCreate}
                onClick={() => createGroup.mutate()}
              >
                {createGroup.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Plus className="h-3.5 w-3.5" />
                )}
                {mode === 'assign' ? 'Anlegen & zuordnen' : 'Anlegen'}
              </Button>
            </div>
          </div>
        )}

        {/* Existing groups list */}
        <div className="space-y-1.5">
          {groupsQuery.isLoading && (
            <p className="px-2 py-3 text-xs text-text-tertiary">Lade Gruppen …</p>
          )}
          {!groupsQuery.isLoading && groups.length === 0 && (
            <p className="px-2 py-6 text-center text-xs text-text-tertiary">
              Noch keine Gruppen angelegt.
            </p>
          )}
          {groups.map((g) => (
            <GroupRow
              key={g.id}
              group={g}
              mode={mode}
              isRenaming={renameId === g.id}
              renameValue={renameValue}
              onStartRename={() => {
                setRenameId(g.id)
                setRenameValue(g.name)
              }}
              onCancelRename={() => {
                setRenameId(null)
                setRenameValue('')
              }}
              onChangeRename={setRenameValue}
              onCommitRename={() => {
                const name = renameValue.trim()
                if (!name || name === g.name) {
                  setRenameId(null)
                  setRenameValue('')
                  return
                }
                renameGroup.mutate({ id: g.id, name })
              }}
              onPickColor={(c) => changeColor.mutate({ id: g.id, color: c })}
              onAssign={() => addMembers.mutate(g.id)}
              assignPending={addMembers.isPending}
              onDelete={async () => {
                const ok = await confirm({
                  title: `Gruppe „${g.name}“ löschen?`,
                  description:
                    'Die Datensätze bleiben erhalten, nur die Gruppen-Zuordnung wird entfernt.',
                  variant: 'danger',
                  confirmLabel: 'Löschen',
                })
                if (ok) removeGroup.mutate(g.id)
              }}
            />
          ))}
        </div>

        {mode === 'manage' && (
          <p className="text-[11px] text-text-tertiary leading-relaxed px-1">
            Hinweis: Datensätze können einer Gruppe über die Mehrfachauswahl in der Liste zugeordnet
            werden.
          </p>
        )}
      </div>
    </SidePanel>
  )
}

function GroupRow({
  group,
  mode,
  isRenaming,
  renameValue,
  onStartRename,
  onCancelRename,
  onChangeRename,
  onCommitRename,
  onPickColor,
  onAssign,
  assignPending,
  onDelete,
}: {
  group: EntityGroupSummary
  mode: 'assign' | 'manage'
  isRenaming: boolean
  renameValue: string
  onStartRename: () => void
  onCancelRename: () => void
  onChangeRename: (v: string) => void
  onCommitRename: () => void
  onPickColor: (c: string | null) => void
  onAssign: () => void
  assignPending: boolean
  onDelete: () => void
}) {
  const [showColorPicker, setShowColorPicker] = useState(false)

  return (
    <div className="group rounded-lg border border-border-subtle bg-bg-overlay/20 hover:border-border-default transition-colors">
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={() => setShowColorPicker((v) => !v)}
          aria-label="Farbe ändern"
          title="Farbe ändern"
          className={cn(
            'inline-block h-3 w-3 rounded-full shrink-0 ring-1 ring-inset ring-black/10 hover:ring-2 hover:ring-accent/60 transition-all',
            groupColorClass(group.color),
          )}
        />
        {isRenaming ? (
          <Input
            value={renameValue}
            onChange={(e) => onChangeRename(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                onCommitRename()
              } else if (e.key === 'Escape') {
                e.preventDefault()
                onCancelRename()
              }
            }}
            autoFocus
            className="h-8 text-sm"
          />
        ) : (
          <button
            type="button"
            onClick={mode === 'assign' ? onAssign : onStartRename}
            disabled={assignPending}
            className="flex-1 min-w-0 text-left"
          >
            <span className="block text-sm font-medium text-text-primary truncate">
              {group.name}
            </span>
            <span className="block text-[11px] text-text-tertiary tabular-nums">
              {group.memberIds.length === 1 ? '1 Eintrag' : `${group.memberIds.length} Einträge`}
            </span>
          </button>
        )}
        <div className="flex items-center gap-0.5 shrink-0">
          {mode === 'assign' && !isRenaming && (
            <Button
              size="sm"
              variant="secondary"
              type="button"
              onClick={onAssign}
              disabled={assignPending}
            >
              {assignPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Plus className="h-3.5 w-3.5" />
              )}
              Zuordnen
            </Button>
          )}
          {mode === 'manage' && !isRenaming && (
            <>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={onStartRename}
                aria-label="Umbenennen"
                title="Umbenennen"
                className="text-text-tertiary hover:text-accent"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={onDelete}
                aria-label="Löschen"
                title="Gruppe löschen"
                className="text-text-tertiary hover:text-danger"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
          {isRenaming && (
            <>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={onCommitRename}
                aria-label="Speichern"
                title="Speichern"
                className="text-accent hover:text-accent"
              >
                <Check className="h-3.5 w-3.5" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={onCancelRename}
                aria-label="Abbrechen"
                title="Abbrechen"
                className="text-text-tertiary hover:text-text-primary"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
        </div>
      </div>
      {showColorPicker && (
        <div className="border-t border-border-subtle px-3 py-2">
          <ColorSwatchPicker
            value={group.color}
            onChange={(c) => {
              onPickColor(c)
              setShowColorPicker(false)
            }}
          />
        </div>
      )}
    </div>
  )
}

function ColorSwatchPicker({
  value,
  onChange,
}: {
  value: string | null | undefined
  onChange: (color: string | null) => void
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {PALETTE.map((c) => {
        const isActive = (c.value ?? null) === (value ?? null)
        return (
          <button
            key={c.value ?? 'none'}
            type="button"
            onClick={() => onChange(c.value)}
            aria-label={`Farbe: ${c.label}`}
            title={c.label}
            className={cn(
              'h-6 w-6 rounded-full inline-flex items-center justify-center transition-all',
              'ring-1 ring-inset ring-black/10',
              groupColorClass(c.value),
              isActive
                ? 'ring-2 ring-accent shadow-sm scale-110'
                : 'hover:ring-2 hover:ring-accent/40 hover:scale-105',
            )}
          >
            {isActive && <Check className="h-3 w-3 text-white drop-shadow-sm" />}
          </button>
        )
      })}
    </div>
  )
}
